import { FC, ReactNode } from "react";
import {
  descriptionVariantClassMap,
  useDescriptionVariant,
} from "../description/use-description-variant";
import { HelpTooltip } from "../help-tooltip";
import { ExternalLink } from "../link";

export interface DisabledCollateralWarningProps {
  assetSymbol: string;
  variant: "form" | "panel";
  description?: ReactNode;
}

export const DisabledCollateralWarning = ({
  assetSymbol,
  variant,
  description: secondaryDescription,
}: DisabledCollateralWarningProps) => {
  const descriptionVariant = useDescriptionVariant();

  const showStaticPrimaryDescription =
    variant === "form" || descriptionVariant === "static";
  const showTooltipPrimaryDescription = !showStaticPrimaryDescription;
  const showStaticSecondaryDescription =
    descriptionVariant === "static" && !!secondaryDescription;
  const showTooltipSecondaryDescription =
    descriptionVariant === "tooltip" && !!secondaryDescription;
  const showStaticDescription =
    showStaticPrimaryDescription || showStaticSecondaryDescription;
  const showTooltip =
    showTooltipPrimaryDescription || showTooltipSecondaryDescription;

  return (
    <div
      className={`flex flex-col gap-5 p-5 text-sm rounded-lg bg-amber-300/5 text-amber-300 ${
        variant === "panel" ? "lg:p-2.5" : ""
      }`}
    >
      <p
        className={`text-xl font-medium ${
          variant === "panel" ? "lg:text-base" : ""
        }`}
      >
        {assetSymbol} is not accepted as collateral{" "}
        {showTooltip && (
          <HelpTooltip className="text-amber-300">
            <div
              className={`flex flex-col gap-5 ${descriptionVariantClassMap["tooltip"]}`}
            >
              {showTooltipPrimaryDescription && (
                <PrimaryDescription assetSymbol={assetSymbol} />
              )}
              {showTooltipSecondaryDescription && secondaryDescription}
            </div>
          </HelpTooltip>
        )}
      </p>
      {showStaticDescription && (
        <div className="flex flex-col gap-5">
          {showStaticPrimaryDescription && (
            <PrimaryDescription assetSymbol={assetSymbol} />
          )}
          {showStaticSecondaryDescription && secondaryDescription}
        </div>
      )}
    </div>
  );
};

interface PrimaryDescriptionProps {
  assetSymbol: string;
}

const PrimaryDescription: FC<PrimaryDescriptionProps> = ({ assetSymbol }) => {
  return (
    <ul className="list-disc flex flex-col gap-2.5 text-sm">
      <li className="ml-5">
        You cannot borrow against {assetSymbol} deposits.
      </li>
      <li className="ml-5">
        {assetSymbol} deposits do not improve your Health Factor.
      </li>
      <li className="ml-5">
        You continue to earn interest based on the current deposit rate. 
      </li>
    </ul>
  );
};

interface DisabledCollateralDescriptionProps {
  description: string;
  link: string;
}

export const DisabledCollateralDescription: FC<
  DisabledCollateralDescriptionProps
> = ({ description, link }) => {
  return (
    <>
      <p className="text-sm">{description}</p>
      <ExternalLink href={link} size="small" />
    </>
  );
};
